import { FetchArgs, FetchBaseQueryError, QueryReturnValue } from "@reduxjs/toolkit/query/react";
import dayjs from "dayjs";

export interface RequestLog {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  error?: FetchBaseQueryError;
  success: boolean;
  body?: unknown;
  response?: unknown;
}

class RequestMonitor {
  private logs: RequestLog[] = [];
  private maxLogs = 100; // Limite de logs mantidos em memória
  private listeners: Set<(logs: RequestLog[]) => void> = new Set();

  logRequest(args: FetchArgs | string, requestId: string, timestamp: string): void {
    const url = typeof args === "string" ? args : args.url;
    const method = typeof args === "string" ? "GET" : args.method || "GET";
    const body = typeof args === "object" && args.body ? args.body : undefined;

    const log: RequestLog = {
      id: requestId,
      timestamp,
      method,
      url,
      success: false,
      body
    };

    this.logs.unshift(log);
    if (this.logs.length > this.maxLogs) {
      this.logs.pop();
    }

    this.notifyListeners();
  }

  logResponse(result: QueryReturnValue<unknown, FetchBaseQueryError, object>, requestId: string, startTimestamp: string): void {
    const log = this.logs.find((l) => l.id === requestId);
    if (!log) return;

    const startTime = dayjs(startTimestamp);
    const endTime = dayjs();
    const duration = endTime.diff(startTime, "millisecond");

    log.duration = duration;
    log.success = !result.error;

    if (result.error) {
      log.error = result.error;
      if ("status" in result.error) {
        log.status = result.error.status as number;
      }
    } else {
      log.response = result.data;
      log.status = 200; // Assumindo sucesso, você pode ajustar conforme necessário
    }

    this.notifyListeners();
  }

  getLogs(): RequestLog[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
    this.notifyListeners();
  }

  subscribe(listener: (logs: RequestLog[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.getLogs()));
  }
}

export const requestMonitor = new RequestMonitor();
